<template>
  <div class="pixso-page pixso-home" :class="{ 'pixso-home--en': isEnglish }">
    <main ref="storyRootRef" class="pixso-home__main" :style="homeMediaStyle">
      <section class="pixso-home__hero" aria-labelledby="home-hero-title">
        <div class="pixso-home__hero-copy">
          <p v-if="showHeroSubtitle" class="pixso-home__hero-subtitle">{{ t('HomeView.hero.subtitle') }}</p>
          <h1 id="home-hero-title" class="pixso-home__hero-title">
            <span v-for="line in heroTitleLines" :key="line">{{ line }}</span>
          </h1>
          <PixsoButton class="pixso-home__hero-cta" to="/solution/collaborative-inspection" />
        </div>
      </section>

      <section class="pixso-home__product-section" aria-labelledby="auto-product-title">
        <div class="pixso-home__product-grid pixso-home__product-grid--auto" data-story-panel>
          <div class="pixso-home__image-panel pixso-home__image-panel--auto" data-story-visual aria-hidden="true"></div>
          <div class="pixso-home__product-copy pixso-home__product-copy--auto" data-story-copy data-text-reveal="off">
            <h2 id="auto-product-title" class="pixso-home__split-title pixso-heading-lg">
              <span
                v-for="(line, lineIndex) in automotiveTitleLines"
                :key="`${line}-${lineIndex}`"
                class="pixso-home__auto-text-line"
              >
                <template v-for="(segment, segmentIndex) in splitMotionText(line)" :key="`auto-title-${lineIndex}-${segmentIndex}`">
                  <span v-if="segment.isSpace" class="pixso-home__auto-text-space">{{ segment.text }}</span>
                  <span v-else class="pixso-home__auto-text-word">
                    <span
                      v-for="(char, charIndex) in segment.chars"
                      :key="`auto-title-${lineIndex}-${segmentIndex}-${charIndex}`"
                      class="pixso-home__auto-text-char"
                    >
                      {{ char }}
                    </span>
                  </span>
                </template>
              </span>
            </h2>
            <p class="pixso-home__product-desc pixso-home__product-desc--auto pixso-body-lg">
              <template v-for="(segment, segmentIndex) in automotiveDescriptionSegments" :key="`auto-desc-${segmentIndex}`">
                <span v-if="segment.isSpace" class="pixso-home__auto-text-space">{{ segment.text }}</span>
                <span v-else class="pixso-home__auto-text-word">
                  <span
                    v-for="(char, charIndex) in segment.chars"
                    :key="`auto-desc-${segmentIndex}-${charIndex}`"
                    class="pixso-home__auto-text-char"
                  >
                    {{ char }}
                  </span>
                </span>
              </template>
            </p>
            <PixsoButton class="pixso-home__auto-mobile-button" to="/product/automotive" />
          </div>
          <div class="pixso-home__auto-reveal" data-auto-reveal data-text-reveal="off">
            <div class="pixso-home__auto-final-copy">
              <PixsoButton class="pixso-home__auto-final-button" to="/product/automotive" />
              <h2
                class="pixso-home__auto-final-title"
                :class="{ 'pixso-home__auto-final-title--en': isEnglish }"
              >
                <span class="pixso-home__auto-final-title-primary">
                  <template v-for="(segment, segmentIndex) in splitMotionText(`${t('HomeView.automotive.title1')}-`)" :key="`auto-final-primary-${segmentIndex}`">
                    <span v-if="segment.isSpace" class="pixso-home__auto-text-space">{{ segment.text }}</span>
                    <span v-else class="pixso-home__auto-text-word">
                      <span
                        v-for="(char, charIndex) in segment.chars"
                        :key="`auto-final-primary-${segmentIndex}-${charIndex}`"
                        class="pixso-home__auto-final-title-char"
                      >
                        {{ char }}
                      </span>
                    </span>
                  </template>
                </span>
                <span>
                  <template v-for="(segment, segmentIndex) in splitMotionText(t('HomeView.automotive.title2'))" :key="`auto-final-secondary-${segmentIndex}`">
                    <span v-if="segment.isSpace" class="pixso-home__auto-text-space">{{ segment.text }}</span>
                    <span v-else class="pixso-home__auto-text-word">
                      <span
                        v-for="(char, charIndex) in segment.chars"
                        :key="`auto-final-secondary-${segmentIndex}-${charIndex}`"
                        class="pixso-home__auto-final-title-char"
                      >
                        {{ char }}
                      </span>
                    </span>
                  </template>
                </span>
              </h2>
            </div>
          </div>
        </div>

        <div class="pixso-home__product-grid pixso-home__product-grid--industry">
          <div class="pixso-home__industry-card" data-story-copy>
            <h2 class="pixso-home__split-title pixso-home__split-title--blur pixso-heading-lg">
              <span class="pixso-home__blur-word" :aria-label="t('HomeView.industry.title1')">
                <span
                  v-for="(char, index) in industryTitleChars"
                  :key="`${char}-${index}`"
                  class="pixso-home__blur-char"
                  :style="{ '--char-index': index }"
                  aria-hidden="true"
                >
                  {{ char }}
                </span>
              </span>
              <span class="pixso-home__blur-word pixso-home__blur-word--secondary" :aria-label="t('HomeView.industry.title2')">
                <template v-for="(segment, segmentIndex) in splitMotionText(t('HomeView.industry.title2'))" :key="`industry-title2-${segmentIndex}`">
                  <span v-if="segment.isSpace" class="pixso-home__auto-text-space">{{ segment.text }}</span>
                  <span v-else class="pixso-home__auto-text-word">
                    <span
                      v-for="(char, charIndex) in segment.chars"
                      :key="`industry-title2-${segmentIndex}-${charIndex}`"
                      class="pixso-home__blur-char"
                      :style="{ '--char-index': industryTitleChars.length + charIndex + segmentIndex }"
                      aria-hidden="true"
                    >
                      {{ char }}
                    </span>
                  </span>
                </template>
              </span>
            </h2>
            <p class="pixso-home__product-desc pixso-body-lg">
              <span v-for="(line, lineIndex) in industryDescriptionLines" :key="`industry-desc-line-${lineIndex}`">
                <template v-for="(segment, segmentIndex) in splitMotionText(line)" :key="`industry-desc-${lineIndex}-${segmentIndex}`">
                  <span v-if="segment.isSpace" class="pixso-home__auto-text-space">{{ segment.text }}</span>
                  <span v-else class="pixso-home__auto-text-word">
                    <span
                      v-for="(char, charIndex) in segment.chars"
                      :key="`industry-desc-${lineIndex}-${segmentIndex}-${charIndex}`"
                      class="pixso-home__industry-desc-char"
                    >
                      {{ char }}
                    </span>
                  </span>
                </template>
              </span>
            </p>
            <PixsoButton to="/product/industry" />
          </div>
          <div class="pixso-home__image-panel pixso-home__image-panel--industry" data-story-visual aria-hidden="true"></div>
        </div>
      </section>

      <section class="pixso-home__scenario" aria-labelledby="scenario-title">
        <div class="pixso-home__section-copy pixso-home__section-copy--scenario" data-story-copy>
          <p class="pixso-eyebrow">{{ t('HomeView.scenario.eyebrow') }}</p>
          <h2 id="scenario-title" class="pixso-heading-xl">{{ t('HomeView.scenario.title') }}</h2>
        </div>

        <div class="pixso-home__scenario-stage pixso-container">
          <div class="pixso-home__scenario-media">
            <swiper
              class="pixso-home__scenario-swiper"
              :modules="scenarioSwiperModules"
              :slides-per-view="1"
              :loop="true"
              :speed="900"
              :autoplay="{ delay: 5000, disableOnInteraction: false }"
              @swiper="handleScenarioSwiper"
              @slideChange="handleScenarioSlideChange"
              @autoplayTimeLeft="handleScenarioAutoplayTimeLeft"
            >
              <swiper-slide v-for="(item, index) in scenarioSlides" :key="item.key">
                <div class="pixso-home__scenario-slide">
                  <img
                    class="pixso-home__scenario-image"
                    :class="`pixso-home__scenario-image--${item.key}`"
                    data-story-visual
                    :src="item.image"
                    :alt="item.alt"
                  />
                </div>
              </swiper-slide>
            </swiper>
            <div class="pixso-home__scenario-mask" aria-hidden="true"></div>
          </div>

          <div class="pixso-home__scenario-copy" data-story-copy>
            <p class="pixso-home__section-desc pixso-body-lg" :class="{ 'pixso-home__section-desc--fixed-breaks': isEnglish }">
              {{ scenarioDescriptionText }}
            </p>
            <PixsoButton to="/solution" />
          </div>

          <div class="pixso-home__scenario-tabs" :aria-label="t('HomeView.scenario.ariaLabel')">
            <button
              v-for="(item, index) in scenarioSlides"
              :key="item.key"
              type="button"
              class="pixso-home__scenario-tab"
              :class="{ 'pixso-home__scenario-tab--active': index === activeScenarioIndex }"
              @click="goToScenario(index)"
            >
              <span class="pixso-home__scenario-ring" :style="getScenarioRingStyle(index)" aria-hidden="true">
                <span class="pixso-home__scenario-ring-core"></span>
              </span>
              <span class="pixso-home__scenario-tab-label" :class="{ 'pixso-home__scenario-tab-label--fixed-breaks': isEnglish }">{{ item.text }}</span>
            </button>
          </div>
        </div>
      </section>

      <section class="pixso-home__delivery" aria-labelledby="delivery-title">
        <div class="pixso-home__section-copy pixso-home__section-copy--delivery" data-story-copy>
          <p class="pixso-eyebrow">{{ t('HomeView.delivery.eyebrow') }}</p>
          <h2 id="delivery-title" class="pixso-heading-xl">{{ t('HomeView.delivery.title') }}</h2>
          <p class="pixso-home__section-desc pixso-body-lg">
            {{ t('HomeView.delivery.description') }}
          </p>
          <PixsoButton to="/deliver" />
        </div>

        <div class="pixso-home__delivery-stage pixso-container" data-story-visual :aria-label="t('HomeView.delivery.imageLabel')">
          <swiper
            class="pixso-home__delivery-swiper"
            :modules="deliverySwiperModules"
            :slides-per-view="1"
            :loop="true"
            :speed="900"
            :autoplay="{ delay: 4000, disableOnInteraction: false }"
            @swiper="handleDeliverySwiper"
            @slideChange="handleDeliverySlideChange"
          >
            <swiper-slide v-for="item in deliverySlides" :key="item.key">
              <div class="pixso-home__delivery-slide">
                <img class="pixso-home__delivery-slide-image" :src="item.image" :alt="t('HomeView.delivery.imageLabel')" />
              </div>
            </swiper-slide>
          </swiper>

          <div class="pixso-home__delivery-dots" :aria-label="t('HomeView.delivery.imageLabel')">
            <button
              v-for="(item, index) in deliverySlides"
              :key="item.key"
              type="button"
              class="pixso-home__delivery-dot"
              :class="{ 'pixso-home__delivery-dot--active': index === activeDeliveryIndex }"
              :aria-label="`${t('HomeView.delivery.imageLabel')} ${index + 1}`"
              @click="goToDelivery(index)"
            ></button>
          </div>
        </div>

        <div
          class="pixso-home__delivery-cards pixso-container"
          data-story-copy
        >
          <article v-for="card in deliveryCards" :key="card.title" class="pixso-home__delivery-card">
            <h3 class="pixso-heading-md">{{ card.title }}</h3>
            <p class="pixso-body-lg">{{ card.description }}</p>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import 'swiper/css'
import PixsoButton from '@/components/pixso/PixsoButton.vue'
import deliveryImage1 from '@/assets/images/pixso-home-official/delivery-1.jpg'
import deliveryImage2 from '@/assets/images/pixso-home-official/delivery-2.jpg'
import deliveryImage3 from '@/assets/images/pixso-home-official/delivery-3.jpg'
import deliveryImage4 from '@/assets/images/pixso-home-official/delivery-4.jpg'
import scenarioAirGroundImage from '@/assets/images/pixso-home-official/scenario-air-ground.jpg'
import scenarioPublicSecurityImage from '@/assets/images/pixso-collaborative-inspection/hero.jpg'
import scenarioSmartDrivingImage from '@/assets/images/pixso-home-official/scenario-smart-driving.jpg'
import scenarioSmartDrivingImageEn from '@/assets/i18n/en/home/scenario-smart-driving.jpg'
import scenarioTraditionalMachineryImage from '@/assets/images/pixso-home-official/scenario-traditional-machinery.jpg'

gsap.registerPlugin(ScrollTrigger)

const { locale, t, te } = useI18n()

const storyRootRef = ref(null)
let storyMatchMedia = null

const scenarioSwiperModules = [Autoplay]
const scenarioSwiper = ref(null)
const activeScenarioIndex = ref(0)
const scenarioProgress = ref(0)
const deliverySwiperModules = [Autoplay]
const deliverySwiper = ref(null)
const activeDeliveryIndex = ref(0)

const isEnglish = computed(() => locale.value === 'en')
const showHeroSubtitle = computed(() => !isEnglish.value)
const heroTitleLines = computed(() => (
  ['line1', 'line2', 'line3']
    .filter((key) => te(`HomeView.hero.${key}`))
    .map((key) => t(`HomeView.hero.${key}`))
))
const industryTitleChars = computed(() => (
  isEnglish.value
    ? [t('HomeView.industry.title1')]
    : Array.from(t('HomeView.industry.title1'))
))
const cjkNoLineStartPunctuation = /^[，。、；：！？）】》〉」』’”〕］｝〗〙〛〞〟…]+$/u

const splitMotionText = (text) => {
  const rawSegments = String(text).match(/\s+|[\u3400-\u9fff\uf900-\ufaff]|[^\s\u3400-\u9fff\uf900-\ufaff]+/gu) || []
  const segments = isEnglish.value
    ? rawSegments
    : rawSegments.reduce((result, segment) => {
      if (
        cjkNoLineStartPunctuation.test(segment) &&
        result.length > 0 &&
        !/^\s+$/.test(result[result.length - 1])
      ) {
        result[result.length - 1] += segment
        return result
      }

      result.push(segment)
      return result
    }, [])

  return segments.map((segment) => ({
    text: segment,
    isSpace: /^\s+$/.test(segment),
    chars: isEnglish.value ? [segment] : Array.from(segment)
  }))
}
const automotiveTitleLines = computed(() => [
  t('HomeView.automotive.title1'),
  t('HomeView.automotive.title2')
])
const automotiveDescriptionSegments = computed(() => splitMotionText(t('HomeView.automotive.description')))
const industryDescriptionLines = computed(() => [
  t('HomeView.industry.descriptionLine1'),
  t('HomeView.industry.descriptionLine2')
])
const homeMediaStyle = computed(() => ({
  '--pixso-home-auto-image': `url(${isEnglish.value ? scenarioSmartDrivingImageEn : scenarioSmartDrivingImage})`
}))

const scenarioSlideDefs = computed(() => [
  {
    key: 'smart-driving',
    image: isEnglish.value ? scenarioSmartDrivingImageEn : scenarioSmartDrivingImage
  },
  {
    key: 'public-security',
    image: scenarioPublicSecurityImage
  },
  {
    key: 'engineering',
    image: scenarioTraditionalMachineryImage
  },
  {
    key: 'uav',
    image: scenarioAirGroundImage
  }
])

const scenarioDescriptionText = computed(() => (
  isEnglish.value
    ? [
        'Relying on multi-unmanned-terminal air-ground collaborative control technology,',
        'we build standardized solutions for park inspection, security inspection, and tunnel corridor inspection.',
        'Bringing together core teams in embedded OS and domestic hardware design,',
        'and working with world-leading precision manufacturing partners,',
        'we provide customers with highly intelligent, highly reliable, and cost-effective application scenario solutions.'
      ].join('\n')
    : t('HomeView.scenario.description')
))

const scenarioSlideTextMap = computed(() => (
  isEnglish.value
    ? {
        'smart-driving': 'Intelligent Driving\nVehicles',
        'public-security': 'Multi-source Collaborative\nInspection',
        'engineering': 'Intelligent Engineering\nMachinery',
        'uav': 'Air-Ground Collaborative\nLogistics'
      }
    : {
        'smart-driving': t('HomeView.scenario.tabs.smartDriving'),
        'public-security': t('HomeView.scenario.tabs.publicSecurity'),
        'engineering': t('HomeView.scenario.tabs.engineering'),
        'uav': t('HomeView.scenario.tabs.uav')
      }
))

const scenarioSlides = computed(() => scenarioSlideDefs.value.map((item) => {
  const text = scenarioSlideTextMap.value[item.key]
  return {
    ...item,
    text,
    alt: text
  }
}))

const deliverySlides = [
  { key: 'delivery-1', image: deliveryImage2 },
  { key: 'delivery-2', image: deliveryImage1 },
  { key: 'delivery-3', image: deliveryImage3 },
  { key: 'delivery-4', image: deliveryImage4 }
]

const handleScenarioSwiper = (swiper) => {
  scenarioSwiper.value = swiper
  activeScenarioIndex.value = swiper.realIndex ?? 0
  scenarioProgress.value = 0
}

const handleScenarioSlideChange = (swiper) => {
  activeScenarioIndex.value = swiper.realIndex ?? 0
  scenarioProgress.value = 0
}

const goToScenario = (index) => {
  activeScenarioIndex.value = index
  scenarioSwiper.value?.slideToLoop(index)
}

const handleScenarioAutoplayTimeLeft = (swiper, timeLeft, progress) => {
  if (swiper.realIndex === activeScenarioIndex.value) {
    scenarioProgress.value = 1 - progress
  }
}

const handleDeliverySwiper = (swiper) => {
  deliverySwiper.value = swiper
  activeDeliveryIndex.value = swiper.realIndex ?? 0
}

const handleDeliverySlideChange = (swiper) => {
  activeDeliveryIndex.value = swiper.realIndex ?? 0
}

const goToDelivery = (index) => {
  activeDeliveryIndex.value = index
  deliverySwiper.value?.slideToLoop(index)
}

const getScenarioRingStyle = (index) => {
  const progress = index === activeScenarioIndex.value ? scenarioProgress.value : 0
  return {
    backgroundImage: `conic-gradient(var(--pixso-color-primary) ${progress * 360}deg, var(--pixso-home-scenario-ring-idle) 0deg)`
  }
}

const deliveryCards = computed(() => [
  {
    title: t('HomeView.delivery.features.production.title'),
    description: t('HomeView.delivery.features.production.description')
  },
  {
    title: t('HomeView.delivery.features.manufacturing.title'),
    description: t('HomeView.delivery.features.manufacturing.description')
  },
  {
    title: t('HomeView.delivery.features.testing.title'),
    description: t('HomeView.delivery.features.testing.description')
  }
])

const getNavOffset = () => {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--pixso-nav-height')
  return Number.parseFloat(value) || 72
}

const setupStoryMotion = () => {
  if (!storyRootRef.value) {
    return
  }

  const panels = gsap.utils.toArray(storyRootRef.value.querySelectorAll('[data-story-panel]'))
  if (panels.length === 0) {
    return
  }

  storyMatchMedia = gsap.matchMedia()
  storyMatchMedia.add('(min-width: 901px) and (prefers-reduced-motion: no-preference)', () => {
    const drawerPanel = panels[0]
    const drawerImage = drawerPanel.querySelector('.pixso-home__image-panel--auto')
    const drawerCopy = drawerPanel.querySelector('.pixso-home__product-copy--auto')
    const autoReveal = drawerPanel.querySelector('.pixso-home__auto-reveal')
    const finalCopy = drawerPanel.querySelector('.pixso-home__auto-final-copy')
    const finalTitleChars = drawerPanel.querySelectorAll('.pixso-home__auto-final-title-char')
    const industryPanel = storyRootRef.value.querySelector('.pixso-home__product-grid--industry')

    if (!drawerImage || !drawerCopy || !autoReveal || !finalCopy || !industryPanel) {
      return undefined
    }

    const drawerTextChars = drawerCopy.querySelectorAll('.pixso-home__auto-text-char')
    const industryChars = industryPanel.querySelectorAll('.pixso-home__blur-char')
    const industryDescChars = industryPanel.querySelectorAll('.pixso-home__industry-desc-char')

    gsap.set(drawerPanel, { transformOrigin: 'center center' })
    gsap.set(drawerImage, { clearProps: 'transform' })
    gsap.set(drawerCopy, { xPercent: 0, autoAlpha: 1 })
    gsap.set(drawerTextChars, { autoAlpha: 0, y: 18, filter: 'blur(14px)' })
    gsap.set(autoReveal, { autoAlpha: 1 })
    gsap.set(finalCopy, { autoAlpha: 0, y: 18 })
    gsap.set(finalTitleChars, { autoAlpha: 0, y: 18, filter: 'blur(14px)' })
    gsap.set(industryChars, { autoAlpha: 0, y: 18, filter: 'blur(14px)' })
    gsap.set(industryDescChars, { autoAlpha: 0, y: 18, filter: 'blur(14px)' })

    const drawerTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: drawerPanel,
        start: () => `top top+=${getNavOffset()}`,
        end: () => `+=${drawerPanel.offsetHeight}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.9,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    })

    drawerTimeline
      .to(drawerCopy, { xPercent: 112, duration: 1, ease: 'none' }, 0)
      .to(drawerCopy, { autoAlpha: 0, duration: 0.08, ease: 'none' }, 0.92)
      .to(
        drawerImage,
        {
          top: 'var(--pixso-page-padding)',
          left: 'var(--pixso-page-padding)',
          width: 'calc(100% - var(--pixso-page-padding) * 2)',
          height: 'calc(100% - var(--pixso-page-padding) * 2)',
          borderRadius: 'var(--pixso-radius-lg)',
          duration: 1,
          ease: 'none'
        },
        0
      )
      .to(finalCopy, { autoAlpha: 1, y: 0, duration: 0.24, ease: 'power2.out' }, 1.02)
      .to(finalTitleChars, {
        autoAlpha: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.28,
        stagger: 0.012,
        ease: 'power3.out'
      }, 1.06)

    const drawerTextTween = gsap.to(drawerTextChars, {
      autoAlpha: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.42,
      stagger: 0.018,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: drawerPanel,
        start: 'top 72%',
        toggleActions: 'play none none reverse'
      }
    })

    const blurTween = gsap.to(industryChars, {
      autoAlpha: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.48,
      stagger: 0.09,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: industryPanel.querySelector('.pixso-home__industry-card'),
        start: 'center 72%',
        toggleActions: 'play none none reverse'
      }
    })

    const industryDescTween = gsap.to(industryDescChars, {
      autoAlpha: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.42,
      stagger: 0.014,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: industryPanel.querySelector('.pixso-home__industry-card'),
        start: 'center 72%',
        toggleActions: 'play none none reverse'
      }
    })

    const triggers = [
      drawerTimeline.scrollTrigger,
      drawerTextTween.scrollTrigger,
      blurTween.scrollTrigger,
      industryDescTween.scrollTrigger
    ]

    return () => {
      triggers.forEach((trigger) => trigger?.kill())
      drawerTextTween.kill()
      blurTween.kill()
      industryDescTween.kill()
      gsap.set(panels, { clearProps: 'all' })
      gsap.set('[data-story-visual], .pixso-home__product-copy--auto, .pixso-home__auto-reveal, .pixso-home__auto-final-copy, .pixso-home__auto-text-char, .pixso-home__auto-final-title-char, .pixso-home__blur-char, .pixso-home__industry-desc-char', { clearProps: 'all' })
    }
  })

  storyMatchMedia.add('(max-width: 900px), (prefers-reduced-motion: reduce)', () => {
    const autoReveal = storyRootRef.value?.querySelector('.pixso-home__auto-reveal')
    gsap.set(panels, { clearProps: 'all' })
    gsap.set('[data-story-visual], .pixso-home__product-copy--auto, .pixso-home__auto-reveal, .pixso-home__auto-final-copy, .pixso-home__auto-text-char, .pixso-home__auto-final-title-char, .pixso-home__blur-char, .pixso-home__industry-desc-char', { clearProps: 'all' })
    if (autoReveal) {
      gsap.set(autoReveal, { autoAlpha: 1 })
    }
  })
}

onMounted(async () => {
  await nextTick()
  setupStoryMotion()
})

watch(locale, async () => {
  storyMatchMedia?.revert()
  await nextTick()
  setupStoryMotion()
  ScrollTrigger.refresh()
})

onBeforeUnmount(() => {
  storyMatchMedia?.revert()
})
</script>

<style scoped>
.pixso-home {
  --pixso-home-hero-height: 1080px;
  --pixso-home-hero-copy-max: 1200px;
  --pixso-home-hero-copy-top-min: var(--pixso-fs-72);
  --pixso-home-hero-copy-top-max: 126px;
  --pixso-home-hero-title-min: var(--pixso-fs-48);
  --pixso-home-hero-title-max: var(--pixso-fs-96);
  --pixso-home-hero-title-line-min: var(--pixso-lh-48);
  --pixso-home-hero-title-line-max: var(--pixso-lh-96);
  --pixso-home-product-height: 1270px;
  --pixso-home-product-top-min: var(--pixso-page-padding);
  --pixso-home-product-desc-max: 760px;
  --pixso-home-story-auto-min: 640px;
  --pixso-home-story-auto-max: 1080px;
  --pixso-home-scenario-top-min: 120px;
  --pixso-home-scenario-top-max: 232px;
  --pixso-home-scenario-bottom-min: var(--pixso-fs-32);
  --pixso-home-scenario-copy-max: 1120px;
  --pixso-home-scenario-copy-inner-max: 1000px;
  --pixso-home-scenario-copy-top-min: 22px;
  --pixso-home-scenario-copy-top-max: 40px;
  --pixso-home-scenario-heading-overlap: -40px;
  --pixso-home-scenario-stage-max: var(--pixso-page-max);
  --pixso-home-scenario-tabs-max: 1200px;
  --pixso-home-scenario-tabs-overlap: -130px;
  --pixso-home-scenario-tabs-bottom: var(--pixso-fs-96);
  --pixso-home-scenario-slide-min: 430px;
  --pixso-home-scenario-slide-offset: 380px;
  --pixso-home-scenario-slide-max: 1080px;
  --pixso-home-scenario-ring-size: 26px;
  --pixso-home-scenario-ring-padding: 2px;
  --pixso-home-scenario-slide-mobile-min: 360px;
  --pixso-home-scenario-slide-mobile-max: 520px;
  --pixso-home-delivery-media-max: 1152px;
  --pixso-home-delivery-image-height: 648px;
  --pixso-home-delivery-image-min: 300px;
  --pixso-home-delivery-card-min: 332px;
  --pixso-home-delivery-top-min: 128px;
  --pixso-home-delivery-top-max: 312px;
  --pixso-home-delivery-bottom-min: 48px;
  --pixso-home-delivery-bottom-max: 96px;
  --pixso-home-delivery-copy-bottom: 88px;
  --pixso-home-delivery-dots-top: -50px;
  --pixso-home-delivery-dots-bottom: var(--pixso-gap-lg);
  --pixso-home-delivery-dot-size: 10px;
  --pixso-home-mobile-panel-min: 420px;
  --pixso-home-mobile-hero-min: 420px;
  --pixso-home-mobile-scenario-slide-height: 340px;
  --pixso-home-mobile-scenario-radius: 24px;
  --pixso-home-mobile-delivery-image-min: 220px;
  --pixso-home-mobile-ring-size: 22px;
  --pixso-home-mobile-section-top: 18px;
  --pixso-home-color-bg-rgb: 0, 0, 0;
  --pixso-home-hero-bg: url("@/assets/images/pixso-home-11-139/Rectangle_11_185.png");
  --pixso-home-hero-bg-position: center bottom;
  --pixso-home-hero-overlay: linear-gradient(180deg, rgba(var(--pixso-home-color-bg-rgb), 1) 8.802%, rgba(var(--pixso-home-color-bg-rgb), 0) 62.447%);
  --pixso-home-scenario-edge-stop: 35.299%;
  --pixso-home-scenario-edge-overlay:
    linear-gradient(90deg, rgba(var(--pixso-home-color-bg-rgb), 1) 0%, rgba(var(--pixso-home-color-bg-rgb), 0) var(--pixso-home-scenario-edge-stop)),
    linear-gradient(0deg, rgba(var(--pixso-home-color-bg-rgb), 1) 0%, rgba(var(--pixso-home-color-bg-rgb), 0) var(--pixso-home-scenario-edge-stop)),
    linear-gradient(270deg, rgba(var(--pixso-home-color-bg-rgb), 1) 0%, rgba(var(--pixso-home-color-bg-rgb), 0) var(--pixso-home-scenario-edge-stop)),
    linear-gradient(180deg, rgba(var(--pixso-home-color-bg-rgb), 1) 0%, rgba(var(--pixso-home-color-bg-rgb), 0) var(--pixso-home-scenario-edge-stop));
  --pixso-home-scenario-ring-idle: rgba(255, 255, 255, 0.35);
  --pixso-home-delivery-dot-idle: rgba(255, 255, 255, 0.45);
}

.pixso-home__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.pixso-home__hero {
  width: 100%;
  margin: 0 auto;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  background-image: var(--pixso-home-hero-overlay), var(--pixso-home-hero-bg);
  background-size: cover, cover;
  background-position: center, var(--pixso-home-hero-bg-position);
  background-repeat: no-repeat;
}

.pixso-home__hero-copy {
  position: absolute;
  top: clamp(var(--pixso-home-hero-copy-top-min), 6.5625vw, var(--pixso-home-hero-copy-top-max));
  left: 50%;
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-home-hero-copy-max));
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(var(--pixso-gap-xs), 0.4167vw, calc(var(--pixso-gap-xs) * 2));
  text-align: center;
}

.pixso-home__hero-subtitle {
  margin: 0;
  color: var(--pixso-color-text);
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-28);
  font-style: italic;
  font-weight: 400;
  line-height: var(--pixso-lh-28);
  white-space: nowrap;
}

.pixso-home__hero-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  margin: 0;
  white-space: nowrap;
}

.pixso-home__hero-cta {
  margin-top: var(--pixso-gap-sm);
}

.pixso-home--en .pixso-home__hero-subtitle,
.pixso-home--en .pixso-home__auto-text-line,
.pixso-home--en .pixso-home__auto-final-title,
.pixso-home--en .pixso-home__blur-char,
.pixso-home--en .pixso-home__industry-desc-char,
.pixso-home--en .pixso-home__product-desc {
  font-family: var(--pixso-font-en);
}

.pixso-home--en .pixso-home__hero-cta {
  font-family: var(--pixso-font-en);
  font-weight: 400;
}

/* English hero title is longer; keep below Chinese fs-96 so lines fit without oversized wrapping */
.pixso-home--en .pixso-home__hero-title {
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

/* English section titles share Automotive heading-lg tokens (fs-64) */
.pixso-home--en .pixso-home__split-title,
.pixso-home--en .pixso-home__section-copy .pixso-heading-xl {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

.pixso-home--en .pixso-home__split-title,
.pixso-home--en .pixso-home__auto-final-title,
.pixso-home--en .pixso-home__product-desc,
.pixso-home--en .pixso-home__blur-word {
  overflow-wrap: normal;
  word-break: keep-all;
  hyphens: manual;
  text-wrap: balance;
}

.pixso-home--en .pixso-home__auto-text-word,
.pixso-home--en .pixso-home__auto-text-char,
.pixso-home--en .pixso-home__auto-final-title-char,
.pixso-home--en .pixso-home__blur-char,
.pixso-home--en .pixso-home__industry-desc-char {
  overflow-wrap: normal;
  word-break: keep-all;
  white-space: nowrap;
}

.pixso-home--en .pixso-home__blur-word {
  display: block;
}

.pixso-home__product-section {
  width: min(100%, var(--pixso-page-max));
  margin: 0 auto;
  padding-top: clamp(var(--pixso-home-product-top-min), 5vw, var(--pixso-fs-96));
}

.pixso-home__product-grid {
  width: min(100%, var(--pixso-page-max));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: min(var(--pixso-home-product-height), 66.1458vw);
  will-change: transform;
}

.pixso-home__product-grid--industry {
  min-height: min(var(--pixso-home-product-height), 66.1458vw);
  gap: var(--pixso-gap-md);
  padding: var(--pixso-page-padding);
  box-sizing: border-box;
}

.pixso-home__image-panel {
  background-size: cover;
  background-repeat: no-repeat;
  will-change: transform;
}

.pixso-home__image-panel--auto {
  margin: var(--pixso-page-padding);
  border-radius: var(--pixso-radius-lg);
  background-image: var(--pixso-home-auto-image);
  background-position: center;
}

.pixso-home__image-panel--industry {
  margin: 0;
  border-radius: var(--pixso-radius-lg);
  background-image: url("@/assets/images/pixso-industry-official/industry-robot.jpg");
  background-position: 12% center;
}

.pixso-home__product-copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--pixso-page-padding);
  text-align: center;
}

.pixso-home__auto-reveal {
  position: absolute;
  inset: var(--pixso-page-padding);
  z-index: 3;
  color: var(--pixso-color-text);
  opacity: 0;
  pointer-events: none;
}

.pixso-home__auto-final-copy {
  position: absolute;
  right: clamp(44px, 4.25vw, 82px);
  bottom: clamp(22px, 4.2vw, 80px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: clamp(var(--pixso-gap-md), 2.8vw, var(--pixso-gap-lg));
  pointer-events: auto;
}

.pixso-home__auto-final-title {
  margin: 0;
  color: var(--pixso-color-text);
  font-size: clamp(var(--pixso-fs-36), 3.75vw, var(--pixso-fs-72));
  font-weight: 700;
  line-height: clamp(var(--pixso-lh-36), 4.48vw, var(--pixso-lh-72));
  letter-spacing: 0;
  white-space: nowrap;
}

.pixso-home__auto-final-title--en {
  max-width: calc(100vw - var(--pixso-page-padding) * 6);
  font-size: clamp(var(--pixso-fs-32), 3vw, var(--pixso-fs-56));
  line-height: clamp(var(--pixso-lh-32), 3.6vw, var(--pixso-lh-56));
  text-align: right;
  white-space: normal;
}

.pixso-home__auto-final-title-primary {
  color: var(--pixso-color-primary);
}

.pixso-home__auto-final-title-char {
  display: inline-block;
  will-change: transform, filter, opacity;
}

.pixso-home__auto-mobile-button {
  display: none;
}

.pixso-home__split-title {
  display: flex;
  flex-direction: column;
  gap: var(--pixso-gap-xs);
  color: var(--pixso-color-text);
  margin: 0 0 var(--pixso-fs-28);
}

.pixso-home__split-title > span:first-child {
  color: var(--pixso-color-primary);
}

.pixso-home__auto-text-line {
  display: block;
}

.pixso-home__auto-text-word {
  display: inline-block;
  overflow-wrap: anywhere;
}

.pixso-home__auto-text-space {
  white-space: pre;
}

.pixso-home__auto-text-char {
  display: inline-block;
  will-change: transform, filter, opacity;
}

.pixso-home__split-title--blur {
  align-items: center;
}

.pixso-home__blur-word {
  display: inline-flex;
  justify-content: center;
  color: var(--pixso-color-primary);
}

.pixso-home__blur-word--secondary {
  color: var(--pixso-color-text);
}

.pixso-home__blur-char {
  display: inline-block;
  will-change: transform, filter, opacity;
}

.pixso-home__product-desc {
  max-width: var(--pixso-home-product-desc-max);
  color: rgba(165, 165, 165, 1);
  font-family: var(--pixso-font-cn);
  font-size: var(--pixso-fs-20);
  font-style: normal;
  font-weight: 700;
  line-height: var(--pixso-lh-20);
  letter-spacing: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.pixso-home__product-desc--auto {
  display: block;
}

.pixso-home__industry-card {
  margin: 0;
  border-radius: var(--pixso-radius-lg);
  background: var(--pixso-color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--pixso-fs-28);
  padding: var(--pixso-page-padding);
  text-align: center;
}

.pixso-home__industry-card .pixso-home__split-title {
  margin-bottom: 0;
}

.pixso-home__scenario {
  width: 100%;
  min-height: min(var(--pixso-home-hero-height), calc(100vh - var(--pixso-nav-height)));
  margin: clamp(var(--pixso-home-scenario-top-min), 12.0833vw, var(--pixso-home-scenario-top-max)) auto 0;
  padding: 0 0 clamp(var(--pixso-home-scenario-bottom-min), 3vw, var(--pixso-gap-lg));
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: var(--pixso-color-bg);
  will-change: transform;
}

.pixso-home__section-copy {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-container));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
}

.pixso-home__section-copy--scenario {
  padding-top: clamp(var(--pixso-fs-28), 3.2vw, 44px);
  padding-bottom: clamp(calc(var(--pixso-gap-sm) - var(--pixso-gap-xs)), 1vw, var(--pixso-fs-14));
  margin-bottom: var(--pixso-home-scenario-heading-overlap);
}

.pixso-home__section-copy h2 {
  margin: 0;
  color: var(--pixso-color-text);
}

.pixso-home__section-desc {
  max-width: var(--pixso-home-scenario-copy-max);
  margin: var(--pixso-fs-16) 0 var(--pixso-gap-md);
  color: var(--pixso-color-muted);
}

.pixso-home__scenario-copy {
  position: absolute;
  top: clamp(var(--pixso-home-scenario-copy-top-min), 2.6vw, var(--pixso-home-scenario-copy-top-max));
  left: 50%;
  z-index: 3;
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-home-scenario-copy-max));
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  pointer-events: none;
}

.pixso-home__scenario-copy .pixso-home__section-desc {
  max-width: var(--pixso-home-scenario-copy-inner-max);
  margin: var(--pixso-fs-14) 0 var(--pixso-fs-28);
  pointer-events: auto;
}

.pixso-home__section-desc--fixed-breaks {
  white-space: pre-line;
}

.pixso-home__scenario-copy .pixso-button {
  pointer-events: auto;
}

.pixso-home__scenario-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(var(--pixso-gap-sm), 1.5vw, var(--pixso-fs-32));
  position: relative;
  z-index: 2;
}

.pixso-home__scenario-tab {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: var(--pixso-gap-sm);
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
  text-decoration: none;
  white-space: nowrap;
}

.pixso-home__scenario-tab:hover,
.pixso-home__scenario-tab:focus-visible,
.pixso-home__scenario-tab--active {
  text-decoration: underline;
  text-underline-offset: 0.18em;
}

.pixso-home__scenario-tab--active {
  color: var(--pixso-color-primary);
  font-weight: 700;
}

.pixso-home__scenario-tab-label--fixed-breaks {
  white-space: pre-line;
  text-align: center;
}

.pixso-home--en .pixso-home__scenario-tab {
  font-size: var(--pixso-fs-18);
  line-height: var(--pixso-lh-18);
}

.pixso-home__scenario-ring {
  width: var(--pixso-home-scenario-ring-size);
  height: var(--pixso-home-scenario-ring-size);
  border-radius: 50%;
  padding: var(--pixso-home-scenario-ring-padding);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: conic-gradient(var(--pixso-color-primary) 0deg, var(--pixso-home-scenario-ring-idle) 0deg);
}

.pixso-home__scenario-ring-core {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--pixso-color-bg);
}

.pixso-home__scenario-stage {
  width: min(100%, var(--pixso-home-scenario-stage-max));
  margin: 0 auto;
  position: relative;
  z-index: 0;
}

.pixso-home__scenario-media {
  position: relative;
  width: 100%;
}

.pixso-home__scenario-swiper {
  width: 100%;
}

.pixso-home__scenario-slide {
  position: relative;
  width: 100%;
  height: min(var(--pixso-home-scenario-slide-max), 56.25vw);
  min-height: var(--pixso-home-scenario-slide-min);
  overflow: hidden;
  background: transparent;
}

.pixso-home__scenario-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1.01);
  will-change: transform;
}

.pixso-home__scenario-mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: var(--pixso-home-scenario-edge-overlay);
}

.pixso-home__scenario-tabs {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-home-scenario-tabs-max));
  margin: 0 auto;
  margin-top: var(--pixso-home-scenario-tabs-overlap);
  padding: 0 var(--pixso-gap-md) var(--pixso-home-scenario-tabs-bottom);
}

.pixso-home__delivery {
  width: 100%;
  margin: 0 auto;
  padding: clamp(var(--pixso-home-delivery-top-min), 16.25vw, var(--pixso-home-delivery-top-max)) 0 clamp(var(--pixso-home-delivery-bottom-min), 18.75vw, var(--pixso-home-delivery-bottom-max));
  background: var(--pixso-color-bg);
  will-change: transform;
}

.pixso-home__section-copy--delivery {
  margin-bottom: var(--pixso-home-delivery-copy-bottom);
}

.pixso-home__delivery-stage {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-home-delivery-media-max));
  height: min(var(--pixso-home-delivery-image-height), 33.75vw);
  min-height: var(--pixso-home-delivery-image-min);
  position: relative;
  overflow: hidden;
  border-radius: var(--pixso-radius-md);
  margin-bottom: var(--pixso-home-delivery-dots-bottom);
  will-change: transform;
}

.pixso-home__delivery-swiper {
  width: 100%;
  height: 100%;
}

.pixso-home__delivery-slide {
  width: 100%;
  height: 100%;
}

.pixso-home__delivery-slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.pixso-home__delivery-dots {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--pixso-gap-sm);
}

.pixso-home__delivery-dot {
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  cursor: pointer;
  border-radius: 50%;
  background: rgba(196, 196, 196, 1);
}

.pixso-home__delivery-dot--active {
  background: rgba(220, 254, 168, 1);
}

.pixso-home__delivery-cards {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-home-delivery-media-max));
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--pixso-gap-md);
}

.pixso-home__delivery-card {
  min-height: var(--pixso-home-delivery-card-min);
  border-radius: var(--pixso-radius-md);
  background: var(--pixso-color-surface);
  padding: var(--pixso-fs-32) var(--pixso-lh-20);
  display: flex;
  flex-direction: column;
  gap: var(--pixso-gap-md);
}

.pixso-home__delivery-card h3 {
  margin: 0;
  color: var(--pixso-color-text);
}

.pixso-home--en .pixso-home__delivery-card h3 {
  overflow-wrap: normal;
  word-break: keep-all;
  hyphens: manual;
}

.pixso-home__delivery-card p {
  margin: 0;
  color: var(--pixso-color-muted);
}

@media (min-width: 901px) and (prefers-reduced-motion: no-preference) {
  .pixso-home__product-copy--auto .pixso-home__auto-text-char {
    opacity: 0;
    transform: translate3d(0, 18px, 0);
    filter: blur(14px);
  }

  .pixso-home__auto-final-title-char {
    opacity: 0;
    transform: translate3d(0, 18px, 0);
    filter: blur(14px);
  }

  .pixso-home__industry-card .pixso-home__industry-desc-char {
    opacity: 0;
    transform: translate3d(0, 18px, 0);
    filter: blur(14px);
  }
}

@media (min-width: 901px) {
  .pixso-home__product-grid--auto {
    position: relative;
    display: block;
    height: var(--pixso-hero-height);
    min-height: 0;
    overflow: hidden;
  }

  .pixso-home__product-grid--auto .pixso-home__image-panel--auto {
    position: absolute;
    top: var(--pixso-page-padding);
    left: var(--pixso-page-padding);
    width: calc(50% - var(--pixso-page-padding) * 2);
    height: calc(100% - var(--pixso-page-padding) * 2);
    margin: 0;
    z-index: 1;
  }

  .pixso-home__product-grid--auto .pixso-home__product-copy--auto {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    width: 50%;
    height: 100%;
    min-height: 0;
  }
}

@media (max-width: 1200px) {
  .pixso-home__scenario-tab {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .pixso-home__scenario-tabs {
    padding-top: var(--pixso-gap-lg);
  }
}

@media (max-width: 900px) {
  .pixso-home__product-grid,
  .pixso-home__product-grid--industry {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .pixso-home__product-grid--industry {
    gap: 0;
    padding: 0;
  }

  .pixso-home__product-grid--industry .pixso-home__image-panel--industry {
    order: -1;
  }

  .pixso-home__auto-reveal {
    display: none;
  }

  .pixso-home__auto-mobile-button {
    display: inline-flex;
    margin-top: var(--pixso-gap-lg);
  }

  .pixso-home__image-panel--auto,
  .pixso-home__image-panel--industry,
  .pixso-home__industry-card {
    margin: var(--pixso-fs-16) var(--pixso-page-padding);
  }

  .pixso-home__image-panel--auto,
  .pixso-home__image-panel--industry {
    min-height: var(--pixso-home-mobile-panel-min);
  }

  .pixso-home__image-panel--auto {
    background-position: 33% center;
  }

  .pixso-home__product-copy,
  .pixso-home__industry-card {
    min-height: var(--pixso-home-mobile-panel-min);
    padding: var(--pixso-gap-lg) var(--pixso-gap-md);
  }

  .pixso-home__scenario {
    min-height: auto;
  }

  .pixso-home__scenario-stage {
    overflow-x: hidden;
  }

  .pixso-home__scenario-tabs {
    width: calc(100% - var(--pixso-page-padding) * 2);
    max-width: var(--pixso-home-scenario-tabs-max);
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 0;
    padding: var(--pixso-fs-36) 0 0;
    gap: var(--pixso-gap-md);
  }

  .pixso-home__scenario-slide {
    height: clamp(var(--pixso-home-scenario-slide-mobile-min), 58vw, var(--pixso-home-scenario-slide-mobile-max));
  }

  .pixso-home__scenario-image--smart-driving {
    object-position: 30% center;
    transform: translateX(-4%) scale(0.92);
  }

  .pixso-home__delivery-cards {
    grid-template-columns: 1fr;
  }

  .pixso-home__delivery-card {
    min-height: auto;
  }

  .pixso-home--en .pixso-home__delivery-card h3 {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }
}

@media (max-width: 768px) {
  .pixso-home__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  /* Mobile EN: keep all section titles on the same scale as Automotive */
  .pixso-home--en .pixso-home__split-title,
  .pixso-home--en .pixso-home__section-copy .pixso-heading-xl {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .pixso-home--en .pixso-home__product-copy--auto .pixso-home__auto-text-line {
    white-space: nowrap;
  }

  .pixso-home--en .pixso-home__product-copy--auto .pixso-home__auto-text-word {
    display: inline-flex;
    white-space: nowrap;
    overflow-wrap: normal;
    word-break: keep-all;
  }

  .pixso-home__hero-subtitle,
  .pixso-home__hero-title {
    white-space: normal;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0 var(--pixso-page-padding);
  }

  .pixso-home__product-desc {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .pixso-home__product-desc br {
    display: none;
  }

  .pixso-home__scenario {
    min-height: auto;
  }

  .pixso-home__section-copy--scenario {
    padding-top: var(--pixso-home-mobile-section-top);
    padding-bottom: var(--pixso-fs-16);
  }

  .pixso-home__scenario-copy {
    top: var(--pixso-home-mobile-section-top);
  }

  /* Mobile: lighter body copy + darker stage so description stays readable over imagery */
  .pixso-home__scenario-copy .pixso-home__section-desc {
    color: rgba(255, 255, 255, 0.92);
  }

  .pixso-home__scenario-mask {
    background:
      linear-gradient(
        180deg,
        rgba(var(--pixso-home-color-bg-rgb), 0.78) 0%,
        rgba(var(--pixso-home-color-bg-rgb), 0.62) 42%,
        rgba(var(--pixso-home-color-bg-rgb), 0.82) 100%
      ),
      var(--pixso-home-scenario-edge-overlay);
  }

  .pixso-home__scenario-tab {
    width: 100%;
    min-height: 72px;
    align-items: center;
    justify-content: center;
    padding: 14px 16px;
    border-radius: var(--pixso-radius-md);
    background: rgba(255, 255, 255, 0.04);
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
    white-space: normal;
    text-align: left;
  }

  .pixso-home__scenario-tab--active {
    background: rgba(220, 254, 168, 0.08);
  }

  .pixso-home__scenario-tab span:last-child {
    flex: 0 1 auto;
  }

  .pixso-home__scenario-ring {
    width: var(--pixso-home-mobile-ring-size);
    height: var(--pixso-home-mobile-ring-size);
  }

  .pixso-home__scenario-slide {
    height: var(--pixso-home-mobile-scenario-slide-height);
    border-radius: var(--pixso-home-mobile-scenario-radius);
  }

  .pixso-home__delivery-stage {
    min-height: var(--pixso-home-mobile-delivery-image-min);
  }
}
</style>
